import type { Role } from "./roles";

/**
 * Клиент справочника контрагентов (E3, раздел «Контрагенты»). Просмотр доступен всем ролям;
 * создание/правка/удаление — ролям регистрации договоров (`contract:register`: делопроизводитель,
 * юрист, админ) — сервер проверяет право на каждом маршруте (матрица 4.3), UI лишь прячет кнопки
 * (правило 5). Форма согласована с сервером по значениям (CounterpartyView из
 * apps/api/src/counterparties/store.ts) — клиент и api типы напрямую не делят.
 */

/**
 * Роли, которым UI показывает кнопки заведения/правки РЕКВИЗИТОВ (право `contract:register`).
 * Это лишь подсказка интерфейса — авторитетную проверку делает сервер. Основание — матрица 4.3.
 */
export const COUNTERPARTY_MUTATE_ROLES: readonly Role[] = ["clerk", "lawyer", "admin"];

/**
 * Роли, которым доступны КОНТРОЛЬ-влияющие операции — переключение аффилированности (В18) и
 * удаление контрагента (право `counterparty:control`, юрист/админ; решение E3). UI-подсказка.
 */
export const COUNTERPARTY_CONTROL_ROLES: readonly Role[] = ["lawyer", "admin"];

/** Может ли пользователь заводить/править реквизиты контрагентов (UI-подсказка). */
export function canMutateCounterparties(roles: readonly Role[]): boolean {
  return roles.some((r) => COUNTERPARTY_MUTATE_ROLES.includes(r));
}

/** Может ли пользователь менять аффилированность и удалять контрагентов (UI-подсказка). */
export function canControlCounterparties(roles: readonly Role[]): boolean {
  return roles.some((r) => COUNTERPARTY_CONTROL_ROLES.includes(r));
}

/** Контактное лицо контрагента. */
export interface CounterpartyContact {
  readonly fio: string;
  readonly position?: string;
  readonly phone?: string;
  readonly email?: string;
}

/** Контрагент — ответ GET /api/counterparties. */
export interface Counterparty {
  readonly id: string;
  readonly name: string;
  readonly inn: string | null;
  readonly kpp: string | null;
  readonly ogrn: string | null;
  readonly address: string | null;
  readonly contacts: CounterpartyContact[];
  readonly affiliated: boolean;
  /** Сколько договоров ссылается на контрагента — при >0 удаление недоступно (В11). */
  readonly contractCount: number;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** Поля формы создания/правки. Пустая строка в необязательном поле = «стереть» (сервер → null). */
export interface CounterpartyInput {
  readonly name: string;
  readonly inn?: string;
  readonly kpp?: string;
  readonly ogrn?: string;
  readonly address?: string;
  readonly affiliated?: boolean;
}

/** Подсказка реквизитов по ИНН из DaData (E3a) — сервер уже отфильтровал поля. */
export interface PartySuggestion {
  readonly inn: string;
  readonly name: string;
  readonly kpp: string | null;
  readonly ogrn: string | null;
  readonly address: string | null;
}

/** Успех со значением либо типизированная причина отказа с готовым сообщением для UI. */
export type ApiResult<T> =
  | { readonly ok: true; readonly value: T }
  | {
      readonly ok: false;
      readonly reason: "unauthorized" | "forbidden" | "conflict" | "validation" | "network";
      readonly message: string;
    };

const JSON_HEADERS = { "Content-Type": "application/json", Accept: "application/json" } as const;

interface ErrorBody {
  error?: string;
  message?: string;
}

async function toFailure(res: Response): Promise<Extract<ApiResult<never>, { ok: false }>> {
  if (res.status === 401) {
    return { ok: false, reason: "unauthorized", message: "Сессия истекла. Войдите заново." };
  }
  const body = (await res.json().catch(() => null)) as ErrorBody | null;
  const message = body?.message;
  if (res.status === 403) {
    return { ok: false, reason: "forbidden", message: message ?? "Недостаточно прав." };
  }
  if (res.status === 409) {
    return { ok: false, reason: "conflict", message: message ?? "Конфликт данных." };
  }
  if (res.status === 400) {
    return { ok: false, reason: "validation", message: message ?? "Проверьте введённые данные." };
  }
  return { ok: false, reason: "network", message: message ?? "Не удалось выполнить операцию." };
}

/** Список контрагентов (GET /api/counterparties). */
export async function fetchCounterparties(): Promise<ApiResult<Counterparty[]>> {
  let res: Response;
  try {
    res = await fetch("/api/counterparties", {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
  } catch {
    return { ok: false, reason: "network", message: "Сервер недоступен. Повторите позже." };
  }
  if (!res.ok) return toFailure(res);
  const body = (await res.json().catch(() => null)) as { counterparties?: Counterparty[] } | null;
  return { ok: true, value: body?.counterparties ?? [] };
}

/** Создание контрагента (POST /api/counterparties). */
export async function createCounterparty(
  input: CounterpartyInput,
): Promise<ApiResult<Counterparty>> {
  return sendCounterparty("/api/counterparties", "POST", input);
}

/** Изменение контрагента (PATCH /api/counterparties/:id). */
export async function updateCounterparty(
  id: string,
  input: CounterpartyInput,
): Promise<ApiResult<Counterparty>> {
  return sendCounterparty(`/api/counterparties/${encodeURIComponent(id)}`, "PATCH", input);
}

async function sendCounterparty(
  url: string,
  method: "POST" | "PATCH",
  input: CounterpartyInput,
): Promise<ApiResult<Counterparty>> {
  let res: Response;
  try {
    res = await fetch(url, {
      method,
      headers: JSON_HEADERS,
      credentials: "same-origin",
      body: JSON.stringify(input),
    });
  } catch {
    return { ok: false, reason: "network", message: "Сервер недоступен. Повторите позже." };
  }
  if (!res.ok) return toFailure(res);
  const body = (await res.json().catch(() => null)) as { counterparty?: Counterparty } | null;
  if (!body?.counterparty) {
    return { ok: false, reason: "network", message: "Неожиданный ответ сервера." };
  }
  return { ok: true, value: body.counterparty };
}

/**
 * Подсказки реквизитов по ИНН (E3a, GET /api/counterparties/suggest). В отличие от остальных
 * вызовов НЕ возвращает ошибку: подсказки — вспомогательные, любой сбой (сеть, выключенная
 * DaData, 4xx/5xx) глушим в пустой список — форма продолжает работать как ручной ввод (мягкая
 * деградация, требование E3a). Наружу уходит только ИНН.
 */
export async function suggestByInn(inn: string): Promise<PartySuggestion[]> {
  try {
    const res = await fetch(`/api/counterparties/suggest?inn=${encodeURIComponent(inn)}`, {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
    if (!res.ok) return [];
    const body = (await res.json().catch(() => null)) as { suggestions?: PartySuggestion[] } | null;
    return Array.isArray(body?.suggestions) ? body.suggestions : [];
  } catch {
    return [];
  }
}

/** Удаление контрагента (DELETE /api/counterparties/:id). Возвращает успех без значения. */
export async function deleteCounterparty(id: string): Promise<ApiResult<null>> {
  let res: Response;
  try {
    res = await fetch(`/api/counterparties/${encodeURIComponent(id)}`, {
      method: "DELETE",
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
  } catch {
    return { ok: false, reason: "network", message: "Сервер недоступен. Повторите позже." };
  }
  if (res.status === 204) return { ok: true, value: null };
  return toFailure(res);
}
